import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AdminAlert } from "../components/AdminAlert";
import { InfoTooltip } from "../components/InfoTooltip";
import { useApi } from "../hooks/useApi";
import {
  ApiError,
  type GitHost,
  type GitHostInput,
  type GitHostPatch,
  type GitHostProvider,
  api,
} from "../lib/api";

interface DraftHost {
  id: string;
  display_name: string;
  provider: GitHostProvider;
  repo_host: string;
  api_base_url: string;
  forge_token: string;
  registry_host: string;
  registry_username: string;
  registry_password: string;
  enabled: boolean;
}

function emptyDraft(): DraftHost {
  return {
    id: "",
    display_name: "",
    provider: "github",
    repo_host: "",
    api_base_url: "",
    forge_token: "",
    registry_host: "",
    registry_username: "",
    registry_password: "",
    enabled: true,
  };
}

function draftFromHost(host: GitHost): DraftHost {
  return {
    id: host.id,
    display_name: host.display_name,
    provider: host.provider,
    repo_host: host.repo_host ?? "",
    api_base_url: host.api_base_url ?? "",
    // Secrets are never returned — left blank, kept unless the admin types a new one.
    forge_token: "",
    registry_host: host.registry_host ?? "",
    registry_username: host.registry_username ?? "",
    registry_password: "",
    enabled: host.enabled,
  };
}

/** Trim and return undefined for blanks, so optional fields are omitted from the payload. */
function opt(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed === "" ? undefined : trimmed;
}

export function AdminGitHosts(): JSX.Element {
  const { t } = useTranslation();
  const [refresh, setRefresh] = useState(0);
  const data = useApi(() => api.admin.gitHosts.list(), [refresh]);
  const [editing, setEditing] = useState<"new" | string | null>(null);
  const [editingHost, setEditingHost] = useState<GitHost | null>(null);
  const [draft, setDraft] = useState<DraftHost>(emptyDraft());
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = (): void => setRefresh((r) => r + 1);

  const startNew = (): void => {
    setEditing("new");
    setEditingHost(null);
    setDraft(emptyDraft());
    setError(null);
    setNotice(null);
  };

  const startEdit = (host: GitHost): void => {
    setEditing(host.id);
    setEditingHost(host);
    setDraft(draftFromHost(host));
    setError(null);
    setNotice(null);
  };

  const cancelEdit = (): void => {
    setEditing(null);
    setEditingHost(null);
    setError(null);
  };

  const onSave = async (): Promise<void> => {
    setError(null);
    setNotice(null);
    setBusy(true);
    // Set host/url/username only when non-empty; token fields only when a new
    // value was typed (blank = keep the stored secret). Shared by create+update.
    const applyOptionalFields = (target: GitHostInput | GitHostPatch): void => {
      const rh = opt(draft.repo_host);
      if (rh) target.repo_host = rh;
      const apiBase = opt(draft.api_base_url);
      if (apiBase) target.api_base_url = apiBase;
      const ft = opt(draft.forge_token);
      if (ft) target.forge_token = ft;
      const regHost = opt(draft.registry_host);
      if (regHost) target.registry_host = regHost;
      const ru = opt(draft.registry_username);
      if (ru) target.registry_username = ru;
      const rp = opt(draft.registry_password);
      if (rp) target.registry_password = rp;
    };
    try {
      if (editing === "new") {
        const input: GitHostInput = {
          id: draft.id.trim(),
          display_name: draft.display_name.trim(),
          provider: draft.provider,
          enabled: draft.enabled,
        };
        applyOptionalFields(input);
        await api.admin.gitHosts.create(input);
      } else if (typeof editing === "string") {
        // Non-secret fields are always sent; host/url/username only when set;
        // token fields only when a new value was typed (blank = keep).
        const patch: GitHostPatch = {
          display_name: draft.display_name.trim(),
          provider: draft.provider,
          enabled: draft.enabled,
        };
        applyOptionalFields(patch);
        await api.admin.gitHosts.update(editing, patch);
      }
      cancelEdit();
      setNotice(t("admin.hosts.saved"));
      reload();
    } catch (e) {
      setError(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (id: string): Promise<void> => {
    if (!window.confirm(t("admin.hosts.confirmDelete", { id }))) return;
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      await api.admin.gitHosts.delete(id);
      setNotice(t("admin.hosts.deleted", { id }));
      reload();
    } catch (e) {
      setError(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  if (data.status === "loading") {
    return <p className="launchpad__hint">{t("admin.loading")}</p>;
  }
  if (data.status === "error") {
    return <p className="launchpad__hint launchpad__hint--error">{data.error.message}</p>;
  }

  const forgeTokenPlaceholder = editingHost?.has_forge_token
    ? t("admin.hosts.field.tokenKeep")
    : "ghp_… / forgejo token";
  const registryPwPlaceholder = editingHost?.has_registry_password
    ? t("admin.hosts.field.tokenKeep")
    : "";

  return (
    <section className="admin">
      <div className="admin__header">
        <h2>{t("admin.hosts.title")}</h2>
        <button type="button" onClick={startNew} disabled={editing !== null}>
          {t("admin.hosts.newHost")}
        </button>
      </div>

      <p className="launchpad__hint">{t("admin.hosts.intro")}</p>

      {error ? <AdminAlert kind="error">{error}</AdminAlert> : null}
      {notice ? <AdminAlert kind="success">{notice}</AdminAlert> : null}

      <table className="admin__table">
        <thead>
          <tr>
            <th>{t("admin.hosts.col.id")}</th>
            <th>{t("admin.hosts.col.displayName")}</th>
            <th>{t("admin.hosts.col.provider")}</th>
            <th>{t("admin.hosts.col.repoHost")}</th>
            <th>{t("admin.hosts.col.registryHost")}</th>
            <th>{t("admin.hosts.col.enabled")}</th>
            <th>{t("admin.col.actions")}</th>
          </tr>
        </thead>
        <tbody>
          {data.data.hosts.map((host) => (
            <tr key={host.id}>
              <td>
                <code>{host.id}</code>
              </td>
              <td>{host.display_name}</td>
              <td>{host.provider}</td>
              <td>{host.repo_host ? <code>{host.repo_host}</code> : "—"}</td>
              <td>{host.registry_host ? <code>{host.registry_host}</code> : "—"}</td>
              <td>
                <span className="badge">
                  {host.enabled ? t("admin.hosts.enabledYes") : t("admin.hosts.enabledNo")}
                </span>
              </td>
              <td className="admin__actions">
                <button type="button" onClick={() => startEdit(host)} disabled={editing !== null}>
                  {t("admin.edit")}
                </button>
                <button
                  type="button"
                  onClick={() => void onDelete(host.id)}
                  disabled={editing !== null || busy}
                >
                  {t("admin.delete")}
                </button>
              </td>
            </tr>
          ))}
          {data.data.hosts.length === 0 ? (
            <tr>
              <td colSpan={7} className="admin__empty">
                {t("admin.hosts.empty")}
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>

      {editing !== null ? (
        <form
          className="admin__form"
          onSubmit={(e) => {
            e.preventDefault();
            void onSave();
          }}
        >
          <h3>
            {editing === "new"
              ? t("admin.hosts.formNew")
              : t("admin.hosts.formEdit", { id: editing })}
          </h3>
          <p className="form__req-note">{t("common.requiredHint")}</p>

          {editing === "new" ? (
            <div className="admin__field">
              <span className="field-head">
                <label htmlFor="host_id">
                  {t("admin.hosts.field.id")}
                  <span className="field-req" aria-hidden="true">
                    *
                  </span>
                </label>
                <InfoTooltip text={t("admin.hosts.field.idHint")} />
              </span>
              <input
                id="host_id"
                type="text"
                value={draft.id}
                onChange={(e) => setDraft({ ...draft, id: e.target.value })}
                placeholder="mindcode"
                required
                pattern="[a-z][a-z0-9-]*[a-z0-9]|[a-z]"
              />
            </div>
          ) : null}

          <div className="admin__field">
            <label htmlFor="host_display_name">
              {t("admin.hosts.field.displayName")}
              <span className="field-req" aria-hidden="true">
                *
              </span>
            </label>
            <input
              id="host_display_name"
              type="text"
              value={draft.display_name}
              onChange={(e) => setDraft({ ...draft, display_name: e.target.value })}
              required
            />
          </div>

          <div className="admin__field">
            <label htmlFor="host_provider">{t("admin.hosts.field.provider")}</label>
            <select
              id="host_provider"
              value={draft.provider}
              onChange={(e) => setDraft({ ...draft, provider: e.target.value as GitHostProvider })}
            >
              <option value="github">GitHub</option>
              <option value="forgejo">Forgejo / Gitea</option>
            </select>
          </div>

          <fieldset className="admin__fieldset">
            <legend>{t("admin.hosts.section.forge")}</legend>
            <span className="hint">{t("admin.hosts.section.forgeHint")}</span>
            <div className="admin__field">
              <span className="field-head">
                <label htmlFor="host_repo_host">{t("admin.hosts.field.repoHost")}</label>
                <InfoTooltip text={t("admin.hosts.field.repoHostHint")} />
              </span>
              <input
                id="host_repo_host"
                type="text"
                value={draft.repo_host}
                onChange={(e) => setDraft({ ...draft, repo_host: e.target.value })}
                placeholder="github.com"
              />
            </div>
            <div className="admin__field">
              <span className="field-head">
                <label htmlFor="host_api_base_url">{t("admin.hosts.field.apiBaseUrl")}</label>
                <InfoTooltip text={t("admin.hosts.field.apiBaseUrlHint")} />
              </span>
              <input
                id="host_api_base_url"
                type="url"
                value={draft.api_base_url}
                onChange={(e) => setDraft({ ...draft, api_base_url: e.target.value })}
                placeholder="https://api.github.com"
              />
            </div>
            <div className="admin__field">
              <span className="field-head">
                <label htmlFor="host_forge_token">{t("admin.hosts.field.forgeToken")}</label>
                <InfoTooltip text={t("admin.hosts.field.forgeTokenHint")} />
              </span>
              <input
                id="host_forge_token"
                type="password"
                value={draft.forge_token}
                onChange={(e) => setDraft({ ...draft, forge_token: e.target.value })}
                placeholder={forgeTokenPlaceholder}
                autoComplete="off"
              />
            </div>
          </fieldset>

          <fieldset className="admin__fieldset">
            <legend>{t("admin.hosts.section.registry")}</legend>
            <span className="hint">{t("admin.hosts.section.registryHint")}</span>
            <div className="admin__field">
              <span className="field-head">
                <label htmlFor="host_registry_host">{t("admin.hosts.field.registryHost")}</label>
                <InfoTooltip text={t("admin.hosts.field.registryHostHint")} />
              </span>
              <input
                id="host_registry_host"
                type="text"
                value={draft.registry_host}
                onChange={(e) => setDraft({ ...draft, registry_host: e.target.value })}
                placeholder="ghcr.io"
              />
            </div>
            <div className="admin__field">
              <label htmlFor="host_registry_username">
                {t("admin.hosts.field.registryUsername")}
              </label>
              <input
                id="host_registry_username"
                type="text"
                value={draft.registry_username}
                onChange={(e) => setDraft({ ...draft, registry_username: e.target.value })}
                autoComplete="off"
              />
            </div>
            <div className="admin__field">
              <span className="field-head">
                <label htmlFor="host_registry_password">
                  {t("admin.hosts.field.registryPassword")}
                </label>
                <InfoTooltip text={t("admin.hosts.field.registryPasswordHint")} />
              </span>
              <input
                id="host_registry_password"
                type="password"
                value={draft.registry_password}
                onChange={(e) => setDraft({ ...draft, registry_password: e.target.value })}
                placeholder={registryPwPlaceholder}
                autoComplete="off"
              />
            </div>
          </fieldset>

          <label className="admin__checkbox">
            <input
              type="checkbox"
              checked={draft.enabled}
              onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })}
            />
            {t("admin.hosts.field.enabled")}
          </label>

          {busy ? (
            <output className="admin__busy" aria-live="polite">
              <span className="spinner" aria-hidden="true" />
              {t("common.saving")}
            </output>
          ) : null}
          <div className="admin__actions">
            <button type="submit" className="primary" disabled={busy}>
              {t("admin.save")}
            </button>
            <button type="button" onClick={cancelEdit} disabled={busy}>
              {t("admin.cancel")}
            </button>
          </div>
        </form>
      ) : null}
    </section>
  );
}

function formatError(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.status === 409) return e.detail ?? e.message;
    if (e.status === 422) return e.detail ?? e.message;
    return e.message;
  }
  return e instanceof Error ? e.message : String(e);
}
